<script setup>
import { ref, watch } from 'vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import SquaremeAPI from 'dashboard/api/squareme';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import {
  escalationForItem,
  formatMoney,
  isZeroMoney,
} from 'dashboard/helper/squareme';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
  allowedTypes: { type: Array, default: () => [] },
});

const emit = defineEmits(['escalate']);

const PAGE_SIZE = 10;

const items = ref([]);
const next = ref(null);
const loading = ref(true);
const loadingMore = ref(false);
const error = ref(false);
const openItemId = ref(null);
const details = ref({});
const detailError = ref({});

const toTime = iso => messageTimestamp(Date.parse(iso) / 1000, 'MMM d, h:mm a');

const signedAmount = item =>
  `${item.direction === 'debit' ? '−' : '+'}${formatMoney(item.amount)}`;

const STATUS_CLASSES = {
  completed: 'bg-n-teal-3 text-n-teal-11',
  pending: 'bg-n-amber-3 text-n-amber-11',
  failed: 'bg-n-ruby-3 text-n-ruby-11',
  reversed: 'bg-n-slate-3 text-n-slate-11',
};

const fetchPage = async cursor => {
  const params = { limit: PAGE_SIZE };
  if (cursor) params.cursor = cursor;
  const { data } = await SquaremeAPI.activity(props.conversationId, params);
  return data;
};

const load = async () => {
  loading.value = true;
  error.value = false;
  openItemId.value = null;
  details.value = {};
  try {
    const data = await fetchPage(null);
    items.value = data.transactions || [];
    next.value = data.next || null;
  } catch {
    error.value = true;
  } finally {
    loading.value = false;
  }
};

const loadMore = async () => {
  loadingMore.value = true;
  try {
    const data = await fetchPage(next.value);
    items.value = [...items.value, ...(data.transactions || [])];
    next.value = data.next || null;
  } catch {
    error.value = true;
  } finally {
    loadingMore.value = false;
  }
};

const toggle = async item => {
  if (openItemId.value === item.id) {
    openItemId.value = null;
    return;
  }
  openItemId.value = item.id;
  if (details.value[item.id]) return;
  detailError.value = { ...detailError.value, [item.id]: false };
  try {
    const { data } = await SquaremeAPI.activityItem(
      props.conversationId,
      item.id
    );
    details.value = { ...details.value, [item.id]: data };
  } catch {
    detailError.value = { ...detailError.value, [item.id]: true };
  }
};

const escalate = item => {
  emit('escalate', {
    ...escalationForItem(item, props.allowedTypes),
    item,
  });
};

watch(() => props.conversationId, load, { immediate: true });

defineExpose({ reload: load });
</script>

<template>
  <section data-squareme-tour="activity" class="flex flex-col gap-2">
    <h4 class="mb-0 text-sm font-medium text-n-slate-12">
      {{ $t('SQUAREME_PANEL.ACTIVITY.TITLE') }}
    </h4>
    <div v-if="loading" class="flex justify-center p-2">
      <Spinner :size="20" class="text-n-brand" />
    </div>
    <p
      v-else-if="error && !items.length"
      data-test-id="squareme-activity-error"
      class="mb-0 text-n-ruby-11"
    >
      {{ $t('SQUAREME_PANEL.ACTIVITY.ERROR') }}
    </p>
    <p v-else-if="!items.length" class="mb-0 text-n-slate-11">
      {{ $t('SQUAREME_PANEL.ACTIVITY.EMPTY') }}
    </p>
    <ul v-else class="flex flex-col gap-1 mb-0 list-none">
      <li
        v-for="item in items"
        :key="item.id"
        data-test-id="squareme-activity-item"
        class="rounded-lg border border-n-weak"
      >
        <button
          type="button"
          class="flex w-full items-start justify-between gap-2 px-3 py-2 text-left"
          :aria-expanded="openItemId === item.id"
          :aria-label="
            openItemId === item.id
              ? $t('SQUAREME_PANEL.ACTIVITY.HIDE_DETAILS')
              : $t('SQUAREME_PANEL.ACTIVITY.SHOW_DETAILS')
          "
          @click="toggle(item)"
        >
          <span class="flex min-w-0 flex-col">
            <span class="truncate text-n-slate-12">{{ item.title }}</span>
            <span class="text-xs text-n-slate-11">
              {{ toTime(item.createdAt) }}
            </span>
          </span>
          <span class="flex flex-col items-end gap-1">
            <span
              class="whitespace-nowrap font-medium"
              :class="
                item.direction === 'debit'
                  ? 'text-n-slate-12'
                  : 'text-n-teal-11'
              "
            >
              {{ signedAmount(item) }}
            </span>
            <span
              class="rounded px-1.5 text-xs"
              :class="STATUS_CLASSES[item.status]"
            >
              {{ $t(`SQUAREME_PANEL.ACTIVITY.STATUS.${item.status}`) }}
            </span>
          </span>
        </button>
        <div
          v-if="openItemId === item.id"
          data-test-id="squareme-activity-detail"
          class="flex flex-col gap-2 border-t border-n-weak px-3 py-2 text-xs"
        >
          <p v-if="detailError[item.id]" class="mb-0 text-n-ruby-11">
            {{ $t('SQUAREME_PANEL.ACTIVITY.DETAIL_ERROR') }}
          </p>
          <Spinner
            v-else-if="!details[item.id]"
            :size="16"
            class="text-n-brand"
          />
          <template v-else>
            <dl class="mb-0 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
              <dt class="text-n-slate-11">
                {{ $t('SQUAREME_PANEL.ACTIVITY.REFERENCE') }}
              </dt>
              <dd class="mb-0 break-all">{{ details[item.id].reference }}</dd>
              <template
                v-if="
                  details[item.id].fee && !isZeroMoney(details[item.id].fee)
                "
              >
                <dt class="text-n-slate-11">
                  {{ $t('SQUAREME_PANEL.ACTIVITY.FEE') }}
                </dt>
                <dd class="mb-0">{{ formatMoney(details[item.id].fee) }}</dd>
              </template>
              <template
                v-for="row in details[item.id].receipt || []"
                :key="`receipt-${row.label}`"
              >
                <dt class="text-n-slate-11">{{ row.label }}</dt>
                <dd class="mb-0 break-words">{{ row.value }}</dd>
              </template>
            </dl>
            <div v-if="Object.keys(details[item.id].breakdown || {}).length">
              <p class="mb-1 font-medium text-n-slate-12">
                {{ $t('SQUAREME_PANEL.ACTIVITY.BREAKDOWN') }}
              </p>
              <dl class="mb-0 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                <template
                  v-for="(money, label) in details[item.id].breakdown"
                  :key="`breakdown-${label}`"
                >
                  <dt class="capitalize text-n-slate-11">{{ label }}</dt>
                  <dd class="mb-0">{{ formatMoney(money) }}</dd>
                </template>
              </dl>
            </div>
            <div v-if="(details[item.id].timeline || []).length">
              <p class="mb-1 font-medium text-n-slate-12">
                {{ $t('SQUAREME_PANEL.ACTIVITY.TIMELINE') }}
              </p>
              <ol class="mb-0 flex list-none flex-col gap-1">
                <li
                  v-for="(event, index) in details[item.id].timeline"
                  :key="`timeline-${index}`"
                  class="flex justify-between gap-2"
                >
                  <span>{{ event.label }}</span>
                  <span class="text-n-slate-11">
                    {{ event.at ? toTime(event.at) : '' }}
                  </span>
                </li>
              </ol>
            </div>
            <Button
              v-if="escalationForItem(item, allowedTypes)"
              size="xs"
              variant="faded"
              color="amber"
              icon="i-lucide-shield-alert"
              data-test-id="squareme-activity-escalate"
              :label="$t('SQUAREME_PANEL.ACTIVITY.ESCALATE_ITEM')"
              @click="escalate(item)"
            />
          </template>
        </div>
      </li>
    </ul>
    <Button
      v-if="next && !loading"
      size="xs"
      variant="ghost"
      color="slate"
      :is-loading="loadingMore"
      :label="$t('SQUAREME_PANEL.ACTIVITY.LOAD_MORE')"
      @click="loadMore"
    />
  </section>
</template>
